import { useRef, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import type { CaptionPreset, LibraryItem, LibraryKind, MadeItem, MadePosts } from "@joogoos/contracts";
import { api } from "../api.js";
import { PublishDialog } from "../studio/Publish.js";
import { Icon } from "../ui/icons.js";
import { NotBuilt, Tab, TabBar } from "./chrome.js";

/** What the API takes for a video, said before a byte is sent: past 100 MB the edge refuses it with no words at all. */
const MAX_VIDEO_MB = 90;

const KINDS: Array<[LibraryKind, string, string]> = [
  ["photo", "Photos & clips", "everything you have uploaded or made"],
  ["music", "Music", "one bed under the whole thing"],
  ["caption", "Caption styles", "how the words look on screen"],
  ["overlay", "Overlays", "lower thirds, end cards, your logo"],
  ["action", "Actions", "call, book, visit"],
];

/**
 * Content (03, 06): the one library — everything a post can be made of that is not the idea.
 *
 * It is deliberately one list with a filter rather than five pages, because the question a person arrives with is
 * "what have I got", not "show me the overlay manager". Yours and ours sit in the same grid, marked, and every row
 * says how many posts use it: in a library this small that is the only ranking worth having.
 */
export function Content({ onOpen, onCalendar }: { onOpen?: ((projectId: string) => void) | undefined; onCalendar?: (() => void) | undefined } = {}) {
  // What has been made comes first (2026-09-25): it is built, and it is what a person opens this tab to find.
  const [kind, setKind] = useState<LibraryKind | "made">("made");
  const library = useQuery({ queryKey: ["content"], queryFn: api.content, enabled: kind !== "made" });
  const items = (library.data?.items ?? []).filter((i) => i.kind === kind);
  // Photos and clips are the media library; the other kinds are still designs, and each says so on its own view
  // rather than the whole tab saying it once and then showing a real grid under the warning.
  const connected = kind !== "made" && !!library.data?.available && (library.data.connected ?? []).includes(kind);
  const pictures = (library.data?.items ?? []).filter((i) => i.kind === "photo").length;

  return (
    <div className="h-full flex flex-col min-h-0">
      <TabBar
        title={kind === "made" ? "Everything you have made" : "Everything your posts are made of"}
        note={kind === "made" ? "every finished post and video, newest first"
          : connected ? `${pictures} ${pictures === 1 ? "picture or clip" : "pictures and clips"}` : "nothing is stored here yet"}
        nav={<div className="flex flex-wrap items-center gap-1.5">
          <Tab on={kind === "made"} title="every finished post and video" onClick={() => setKind("made")}>Made</Tab>
          {KINDS.map(([k, label, note]) => (
            <Tab key={k} on={kind === k} title={note} onClick={() => setKind(k)}>{label}</Tab>
          ))}
        </div>}
      >
        <ImportVideo onDone={() => setKind("made")} />
      </TabBar>

      <div className="flex-1 min-h-0 overflow-y-auto px-6 py-5">
        {kind === "made" ? <Made onOpen={onOpen} onCalendar={onCalendar} /> : (
          <>
            {library.data && !connected ? <NotBuilt what={KINDS.find(([k]) => k === kind)![1].toLowerCase()} note={library.data.note} /> : null}
            {connected && !items.length ? <p className="text-[13px] text-muted">Nothing here yet. Pictures you upload in the Studio, and the ones it makes for you, land here.</p> : null}
            {kind === "caption" ? <Captions items={items} /> : <Grid kind={kind} items={items} />}
          </>
        )}
      </div>
    </div>
  );
}

/**
 * Bringing in a video made somewhere else — on a phone, by an editor — so it can be posted from here like anything the
 * Studio made (2026-09-26, the owner: *"an import button in the content to upload video so that we can post them"*).
 * It lands in Made, because it is finished: nothing in Joogoos works on it, and importing it costs nothing.
 */
function ImportVideo({ onDone }: { onDone: () => void }) {
  const qc = useQueryClient();
  const input = useRef<HTMLInputElement>(null);
  const [tooBig, setTooBig] = useState<string | null>(null);
  const upload = useMutation({
    mutationFn: api.upload,
    onSuccess: () => { void qc.invalidateQueries({ queryKey: ["made"] }); onDone(); },
  });
  const pick = (file: File | undefined) => {
    if (!file) return;
    setTooBig(file.size > MAX_VIDEO_MB * 1024 * 1024 ? `${file.name} is ${Math.round(file.size / 1024 / 1024)} MB; a video can be up to ${MAX_VIDEO_MB} MB.` : null);
    if (file.size <= MAX_VIDEO_MB * 1024 * 1024) upload.mutate(file);
  };
  const problem = tooBig ?? upload.error?.message ?? null;
  return (
    <>
      {problem ? <span role="alert" className="text-xs text-bad max-w-[320px]">{problem}</span> : null}
      <input ref={input} type="file" accept="video/mp4,video/quicktime,.mp4,.mov" hidden aria-label="Video to import"
        onChange={(e) => { pick(e.target.files?.[0]); e.target.value = ""; }} />
      <button onClick={() => input.current?.click()} disabled={upload.isPending}
        className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-ink text-white text-xs font-semibold disabled:opacity-50">
        <Icon name="upload" size={13} />{upload.isPending ? "Importing…" : "Import a video"}
      </button>
    </>
  );
}

/** Every finished piece, playable where it sits, with what a person does next: post it, open it, or take it away. */
function Made({ onOpen, onCalendar }: { onOpen?: ((projectId: string) => void) | undefined; onCalendar?: (() => void) | undefined }) {
  const made = useQuery({ queryKey: ["made"], queryFn: api.made });
  if (made.isLoading) return <p className="text-[13px] text-muted">Loading…</p>;
  if (made.error) return <p role="alert" className="text-[13px] text-bad">{String(made.error)}</p>;
  const items = made.data?.items ?? [];
  if (!items.length) return <p className="text-[13px] text-muted max-w-md leading-relaxed">Nothing finished yet. What you make in the Studio lands here the moment it is rendered, and a video you import lands here too.</p>;
  return (
    <div className="grid gap-4 grid-cols-[repeat(auto-fill,minmax(220px,1fr))]">
      {items.map((i) => <MadeCard key={i.mediaId} item={i} onOpen={onOpen} onCalendar={onCalendar} />)}
    </div>
  );
}

function MadeCard({ item, onOpen, onCalendar }: { item: MadeItem; onOpen?: ((projectId: string) => void) | undefined; onCalendar?: (() => void) | undefined }) {
  const video = item.mime.startsWith("video/");
  const ratio = item.width && item.height ? `${item.width} / ${item.height}` : "9 / 16";
  const when = new Date(item.madeAt).toLocaleDateString(undefined, { day: "numeric", month: "short" });
  const seconds = item.durationMs ? `${Math.round(item.durationMs / 1000)} s · ` : "";
  const [publishing, setPublishing] = useState(false);
  const qc = useQueryClient();
  // Only an imported video is taken away from here: a rendered one belongs to its project, and goes with it.
  const remove = useMutation({ mutationFn: () => api.deleteMedia(item.mediaId), onSuccess: () => void qc.invalidateQueries({ queryKey: ["made"] }) });
  const imported = item.projectId === null;
  const link = "text-xs font-medium text-muted hover:text-ink underline-offset-2 hover:underline";
  return (
    <figure className="bg-panel border border-line rounded-[10px] overflow-hidden flex flex-col">
      {/* The picture fills this box and keeps its shape: sized by its content, a 9:16 video drew 407 px in a 360 px
          box and covered the card's own title (dev box, 2026-09-25). */}
      <div className="bg-stage relative overflow-hidden" style={{ aspectRatio: ratio, maxHeight: 360 }}>
        {video
          // The poster frame until somebody presses play: a grid of finished videos does not fetch every one of them.
          ? <video src={item.url} poster={item.posterUrl} controls preload="none" playsInline className="absolute inset-0 w-full h-full object-contain" aria-label={item.title} />
          : <img src={item.posterUrl} alt={item.title} loading="lazy" decoding="async" className="absolute inset-0 w-full h-full object-contain" />}
      </div>
      <figcaption className="p-3 flex flex-col gap-2">
        <div className="min-w-0">
          <div className="text-[13.5px] font-semibold truncate" title={item.title}>{item.title}</div>
          <div className="text-[11.5px] text-muted">{imported ? "Imported" : item.kind === "post" ? "Post" : "Video"} · {seconds}{when}{imported ? "" : ` · $${item.spentUsd}`}</div>
          <PostsLine posts={item.posts} onCalendar={onCalendar} />
        </div>
        {/* The same Post button the Studio's result carries, through the same dialog, which asks now or later: nothing
            reaches a channel without a person looking at it first (ADR 0011). */}
        <div className="flex gap-2">
          <button onClick={() => setPublishing(true)} className="px-3 py-1.5 rounded-lg bg-coral text-white text-xs font-semibold">Post</button>
        </div>
        <div className="flex gap-3">
          {onOpen && item.projectId ? <button onClick={() => onOpen(item.projectId!)} className={link}>Open</button> : null}
          {/* `?download=1` asks for the file as an attachment: `download` is ignored once the file comes from storage. */}
          <a href={`${item.url}?download=1`} download className={link}>Download</a>
          {imported ? <button onClick={() => { if (confirm("Remove this video from Joogoos? Posts already scheduled with it still go out.")) remove.mutate(); }} disabled={remove.isPending} className={link}>Remove</button> : null}
        </div>
      </figcaption>
      {publishing ? (
        <PublishDialog piece={{ projectId: item.projectId, media: { id: item.mediaId, kind: video ? "video" : "image", durationMs: item.durationMs, bytes: item.bytes } }}
          onClose={() => setPublishing(false)} />
      ) : null}
    </figure>
  );
}

/**
 * Where a piece has gone, in one line, and the way to the Calendar where each post is (2026-09-29, the owner: *"in
 * content we need to know the content status, and when posted we can click on it so it goes to the calendar"*).
 */
function PostsLine({ posts, onCalendar }: { posts: MadePosts; onCalendar?: (() => void) | undefined }) {
  const day = (iso: string) => new Date(iso).toLocaleDateString(undefined, { day: "numeric", month: "short" });
  const moment = (iso: string) => new Date(iso).toLocaleString(undefined, { day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" });
  const parts: Array<{ text: string; bad?: boolean }> = [];
  if (posts.published) parts.push({ text: `Posted on ${posts.published}${posts.lastAt ? ` · ${day(posts.lastAt)}` : ""}` });
  if (posts.going) parts.push({ text: `${posts.going} to go${posts.nextAt ? ` · next ${moment(posts.nextAt)}` : ""}` });
  if (posts.toApprove) parts.push({ text: `${posts.toApprove} to approve` });
  if (posts.failed) parts.push({ text: `${posts.failed} stopped`, bad: true });
  if (!parts.length) return <div className="text-[11.5px] text-muted">Not posted yet</div>;
  return (
    <div className="text-[11.5px] flex flex-wrap items-center gap-x-1.5">
      {parts.map((p, i) => <span key={p.text} className={p.bad ? "text-bad font-medium" : "text-ink"}>{i ? "· " : ""}{p.text}</span>)}
      {onCalendar ? <button onClick={onCalendar} className="text-muted hover:text-ink underline underline-offset-2">See on the Calendar</button> : null}
    </div>
  );
}

function Grid({ kind, items }: { kind: LibraryKind; items: LibraryItem[] }) {
  if (!items.length) return null;
  return (
    <div className={`grid gap-3 ${kind === "photo" ? "grid-cols-[repeat(auto-fill,minmax(150px,1fr))]" : "grid-cols-[repeat(auto-fill,minmax(260px,1fr))]"}`}>
      {items.map((i) => (kind === "photo" ? <Picture key={i.id} item={i} /> : <Row key={i.id} item={i} />))}
    </div>
  );
}

function Picture({ item }: { item: LibraryItem }) {
  return (
    <figure className="flex flex-col gap-1.5">
      <div className="relative rounded-[10px] overflow-hidden aspect-[4/5] bg-line">
        {!item.url ? null : item.durationMs ? (
          <video src={item.url} muted playsInline preload="metadata" aria-label={item.name} className="w-full h-full object-cover" />
        ) : (
          <img src={item.url} alt={item.name} loading="lazy" decoding="async" className="w-full h-full object-cover" />
        )}
        <Origin origin={item.origin} />
      </div>
      <figcaption className="text-[11.5px] text-muted truncate">{item.name}</figcaption>
    </figure>
  );
}

function Row({ item }: { item: LibraryItem }) {
  return (
    <div className="bg-panel border border-line rounded-[10px] p-3 flex items-center gap-3">
      <button className="w-9 h-9 rounded-full bg-ink text-white grid place-items-center shrink-0" title={`Preview ${item.name}`} aria-label={`Preview ${item.name}`}>
        <Icon name={item.kind === "music" ? "play" : item.kind === "action" ? "send" : "image"} size={14} />
      </button>
      <div className="min-w-0 flex-1">
        <div className="text-[13.5px] font-semibold truncate">{item.name}</div>
        <div className="text-[11.5px] text-muted truncate">{item.detail}</div>
      </div>
      <Used n={item.usedIn} />
    </div>
  );
}

/**
 * The caption styles, drawn rather than described — which is the one thing the old composer got right about them
 * (08). Four presets, the same words in each, and the accent colour beside them.
 */
function Captions({ items }: { items: LibraryItem[] }) {
  if (!items.length) return null;
  return (
    <div className="grid grid-cols-[repeat(auto-fill,minmax(280px,1fr))] gap-3">
      {items.map((i) => (
        <div key={i.id} className="bg-panel border border-line rounded-[10px] overflow-hidden flex flex-col">
          <div className="bg-stage aspect-[16/7] grid place-items-center px-4">
            <Sample preset={i.preset ?? "basic"} color={i.color ?? "#f64b2b"} />
          </div>
          <div className="p-3 flex items-center gap-3">
            <div className="min-w-0 flex-1">
              <div className="text-[13.5px] font-semibold">{i.name}</div>
              <div className="text-[11.5px] text-muted truncate">{i.detail}</div>
            </div>
            <Used n={i.usedIn} />
          </div>
        </div>
      ))}
    </div>
  );
}

const WORDS = ["Fresh", "every", "Saturday"];

function Sample({ preset, color }: { preset: CaptionPreset; color: string }) {
  const stroke = { textShadow: "0 2px 6px rgba(0,0,0,.65)" };
  if (preset === "hormozi") {
    return (
      <span className="text-[17px] font-extrabold uppercase tracking-tight text-white" style={stroke}>
        {WORDS.map((w, i) => <span key={w} style={i === WORDS.length - 1 ? { color } : undefined}>{w} </span>)}
      </span>
    );
  }
  if (preset === "wrap") {
    return <span className="text-[15px] font-semibold text-white px-2 py-0.5 rounded-[5px]" style={{ backgroundColor: color }}>{WORDS.join(" ")}</span>;
  }
  if (preset === "bold") {
    return <span className="text-[19px] font-black uppercase tracking-tight" style={{ color, ...stroke }}>{WORDS.join(" ")}</span>;
  }
  return <span className="text-[15px] font-semibold text-white" style={stroke}>{WORDS.join(" ")}</span>;
}

function Used({ n }: { n: number }) {
  return (
    <span className="shrink-0 text-[11px] text-muted tabular-nums">
      {n ? `in ${n} post${n > 1 ? "s" : ""}` : "unused"}
    </span>
  );
}

/**
 * Where a file came from, said on the file. Six months on, nobody remembers which pictures they chose and which
 * were taken off their own website when we first read it — so the ones that were say so, for good.
 */
const ORIGIN: Partial<Record<LibraryItem["origin"], string>> = {
  joogoos: "ours", website: "your site", google: "Google", web: "the web", generated: "made for you",
};

function Origin({ origin }: { origin: LibraryItem["origin"] }) {
  const label = ORIGIN[origin];
  if (!label) return null;
  return <span className="absolute top-1.5 left-1.5 px-1.5 py-0.5 rounded bg-black/55 text-white text-[9px] font-bold uppercase tracking-wide">{label}</span>;
}
